<script>
  import MTableBody from 'components/Data/Tables/MTable/MTableBody';
  import MTableCellContainer from 'components/Data/Tables/MTable/MTableCellContainer';
  import MTableCellDefault from 'components/Data/Tables/MTable/cells/MTableCellDefault';
  import MTableHeader from 'components/Data/Tables/MTable/MTableHeader';
  import MTableHeaderDefault from 'components/Data/Tables/MTable/headers/MTableHeaderDefault';
  import MTableSelectableMixin from 'components/Data/Tables/MTable/mixins/MTableSelectableMixin';
  import MTableSortableMixin from 'components/Data/Tables/MTable/mixins/MTableSortableMixin';
  import MTableResizableMixin from 'components/Data/Tables/MTable/mixins/MTableResizableMixin';
  import MTableDropdownMixin from 'components/Data/Tables/MTable/mixins/MTableDropdownMixin';
  import MTableHeaderMixin from 'components/Data/Tables/MTable/mixins/MTableHeaderMixin';
  import MTableWidthMixin from 'components/Data/Tables/MTable/mixins/MTableWidthMixin';
  import MTableStickableMixin from 'components/Data/Tables/MTable/mixins/MTableStickableMixin';

  import MTableHooks from 'components/Data/Tables/MTable/MTable.Hooks';
  import MTablePreprocessors from 'components/Data/Tables/MTable/MTable.Preprocessors';
  import MTableCommon from 'components/Data/Tables/MTable/MTable.Common';
  import MTableEvents from 'components/Data/Tables/MTable/MTable.Events';
  import _ from 'lodash';
  import MTableRow from 'components/Data/Tables/MTable/MTableRow';
  import MTableHeaderRow from 'components/Data/Tables/MTable/MTableHeaderRow';
  import MTableCellSpacer from 'components/Data/Tables/MTable/MTableCellSpacer';
  import NestedTableMixin from 'components/Data/Tables/MTable/mixins/NestedTableMixin';

  _.noConflict();

  const columnDefaultParams = {

    // key of element in row Object
    itemKey: '',

    // text in header
    header: '',

    // hide column ???????
    showColumn: true,

    // component for cell
    cellComponent: MTableCellDefault,

    // component for header
    headerComponent: MTableHeaderDefault,

    // hint for columns (shown in column header)
    headerHint: null,

    // width of header hint popup
    headerHintWidth: '168px',

    // align cell content and cell header content
    align: 'right',

    // name of cell group (used in MTable settings)
    groupName: null,

    // minimal width of column in px
    minWidth: 50,

    // allow column resize
    resizable: true,

    // will actually resize column after mouseUp
    lazyResize: false,

    // do not change width of column when page is resized (or table must fill full width)
    static: false,

    // enable sorting for current column (emits event)
    sortable: false,

    // default direction for sorting
    defaultSortDirection: 'none',

    // first sorting choice (after first click on header to sort it)
    firstSortDirection: 'asc',

    // sorting priority (for table with multiple sorting columns)
    sortPriority: 1,

    // table header icon
    icon: null,

    // column sticks on horizontal scroll
    sticky: false,

    // determine if column can open dropdown (for tables with dropdown)
    isDropdownEnabledPredicate: () => true,

    // column triggers dropdown open
    triggersDropdown: false,

    // column event listeners
    on: {},

    // headers event listeners
    onHeader: {},

    uniqueKeyAttr: 'id',

    heightInCells: 1,

    widthInCells: 1
  };


  /**
   * @exports MTable
   * @class MTable
   * @mixin MTableSelectableMixin
   * @mixin MTableResizableMixin
   * @mixin MTableHeaderMixin
   * @mixin MTableSortableMixin
   * @mixin MTableStickableMixin
   * @mixin MTableWidthMixin
   *
   * base table component. Incapsulate logic about:
   * 1. rendering {@link MTableHeader} for every {@link MTableColumn} in default slot
   * 2. rendering {@link MTableRow} for every row of {@link data} array
   * 3. rendering {@link MTableCellContainer} for every data entry in one row
   *
   * @typedef {function(MTableColumn[], CreateElement)} ColumnPreprocessor
   * @typedef {function(Array.<Object>[])} DataPreprocessor
   */
  export default {
    name: 'MTable',
    mixins: [
      MTableHooks,
      MTablePreprocessors,
      MTableCommon,
      MTableEvents,
      MTableWidthMixin,
      MTableStickableMixin,
      MTableResizableMixin,
      MTableHeaderMixin,
      MTableSortableMixin,
      MTableSelectableMixin,
      MTableDropdownMixin,
      NestedTableMixin
    ],
    inheritAttrs: false,
    props: {

      // table columns
      columns: {
        type: Array,
        default: Array
      },

      // data which is displayed in table
      data: {
        type: Array,
        default: Array
      },

      enabledKey: {
        type: String,
        default: null
      },

      // key of attribute that identifies row
      uniqueKeyAttr: {
        type: String,
        default: 'id'
      },


      /**
       * function that makes rows grayed should accept tableRow object
       * and should return array of colors [backgroundColor, textColor]
       * items is `null` if not color should be applied or color
       */
      colorFunction: {
        type: Function,
        default: null
      },

      /** other table params eg column sizes */
      params: {
        type: Object,
        default: Object
      },

      /** Make first row bold (summary row) */
      withSummaryRow: {
        type: Boolean,
        default: false
      },
      allowHeaderWordWrap: {
        type: Boolean,
        default: false
      },
      columnLazyResize: {
        type: Boolean,
        default: false
      },
      fontSize: {
        type: String,
        default: 'S'
      }
    },
    data () {
      return {

        /**
         * other table params eg column sizes
         * copied into data to be able to change it into mixins and {@emit 'update-params'}
         * @type {Object}
         */
        tableParams: this.params,

        /**
         * props which will be passed into each {@link MTableHeader} component. It used by mixins
         * @type {Object.<String, *>}
         */
        headerProps: {},

        /**
         * props which will be passed into each {@link MTableCellContainer} component. It used by mixins
         * @type {Object.<String, *>}
         */
        cellProps: {},

        /**
         * Store ordered column indexes to save initial order of columns
         * @type {Array<String>}
         */
        orderedIndexes: [],

        /**
         * :class prop for {@link MTableBody}. Used by mixins
         * @type {Object.<String, Boolean|function(): Boolean>}
         */
        class: {
          'm-table-font-size-s': this.fontSize === 'S',
          'm-table-font-size-m': this.fontSize === 'M'
        }
      };
    },
    computed: {
      dataWithoutSummary () {
        if (this.withSummaryRow && this.data.length > 0) {

          // remove first row (summary row)
          return this.data.slice(1);
        }
        return this.data;
      },
      superHeaderParams () {
        const parentNodes = this.columns.filter(c => Boolean(c.children));

        const params = {};
        parentNodes.forEach(parent => {
          const columnId = parent.itemKey;
          const childColumnIds = parent.children.map(c => c.itemKey);

          const parentWidth = childColumnIds.reduce((acc, curr) => {
            const childParams = this.tableParams[curr];
            if (childParams && childParams.width) {
              return acc + childParams.width;
            }
            return acc;
          }, 0);
          params[columnId] = {
            width: parentWidth
          };
        });
        return params;
      },
      superHeaderNodeKeys () {
        const parentNodes = this.columns.filter(c => Boolean(c.children));
        return parentNodes.map(c => c.itemKey);
      },
      computedColumns () {
        return this.getComputedColumns(this.columns);
      },
      headersPrepared () {
        const tableHasSubheaders = this.computedColumns.some(c => c.children);
        if (!tableHasSubheaders) {
          return [this.computedColumns.map(c => this.fillDefaultColumnData(c)), []];
        }

        const columns = _.cloneDeep(this.computedColumns);

        const fLevel = [];
        const sLevel = [];

        columns.forEach(c => {
          c.multilineHeader = true;
          if (c.children) {
            c.isSuperHeader = true;
            fLevel.push(c);
            c.widthInCells = c.children.length;
            c.children.forEach(child => {
              child.multilineHeader = true;
            });
            sLevel.push(...c.children);
          } else {
            c.heightInCells = 2;
            fLevel.push(c);
          }
        });

        return [
          fLevel.map(c => this.fillDefaultColumnData(c)),
          sLevel.map(c => this.fillDefaultColumnData(c))
        ];

      },
      headersFlat () {
        const columns = _.cloneDeep(this.computedColumns);

        let secondLevelHeaders = [];
        let addBorderToEl = false;

        const tableHasSubheaders = columns.some(c => c.children);

        /*
         * reverse array to use flag that adds border to "prev" column
         * that hack prevents us from repeating iteration over previous column
         */
        columns.slice().reverse().
          forEach((column, index) => {

            const isLast = index === 0;
            const isFirst = index === columns.length - 1 && !this.selectable;

            const childColumns = column.children || [];

            if (childColumns.length > 0) {
              this.configureFirstLevelHeader(column, false, addBorderToEl, isFirst, isLast);
              secondLevelHeaders = [
                ...secondLevelHeaders,
                ...this.configureSecondLevelHeaders(column, childColumns, index, isFirst, isLast)
              ];

              addBorderToEl = true;
            } else {
              this.configureFirstLevelHeader(column, tableHasSubheaders, addBorderToEl, isFirst, isLast);
              addBorderToEl = false;
            }
          });

        secondLevelHeaders.reverse();

        const columnsList = [...columns, ...secondLevelHeaders];

        this.columnsPreprocessors.forEach(preprocess => {
          preprocess(columnsList);
        });

        return columnsList.map(c => this.fillDefaultColumnData(c));
      },
      columnsMap () {
        return _.keyBy(this.headersFlat, 'itemKey');
      },
      columnsFlat () {
        const realColumns = this.computedColumns.map(c => {
          if (c.children) {
            return c.children;
          }
          return c;
        }).flat();
        return realColumns.map(c => this.columnsMap[c.itemKey]).map(c => this.fillDefaultColumnData(c));
      },
      firstColumnKey () {
        const firstColumn = this.columnsFlat[0];
        if (firstColumn) {
          return firstColumn.itemKey;
        }
        return null;
      },
      lastColumnKey () {
        const lastColumn = this.columnsFlat[this.columnsFlat.length - 1];
        if (lastColumn) {
          return lastColumn.itemKey;
        }
        return null;
      },

      orderedUniqueValues () {
        return this.data.map(i => i ? i[this.uniqueKeyAttr] : null).filter(i => Boolean(i));
      },

      // unique ordered hash to detect changes in data (sorting or page changing)
      uniqueOrderedHash () {
        return this.orderedUniqueValues.join();
      }
    },
    watch: {
      params (val) {
        this.tableParams = val;
      },

      data () {
        this.afterDataUpdate();
      }
    },

    methods: {
      fillDefaultColumnData (column) {
        return {
          ...columnDefaultParams,
          ...column
        };
      },
      configureFirstLevelHeader (column, doubleHeightHeader, addBorder, isFirst, isLast) {
        column.hasRightBorder = addBorder || column.hasRightBorder;

        column.heightInCells = doubleHeightHeader ? 2 : 1;
        column.doubleHeight = doubleHeightHeader;
        column.isFirst = isFirst;
        column.isLast = isLast;

        this.configureFirstLevelHeaderHook.forEach(i => i(column, doubleHeightHeader, addBorder));
      },

      configureSecondLevelHeaders (column, childColumns, groupIndex, parentIsFirst, parentIsLast) {
        column.widthInCells = childColumns.length;

        // if it is last element no border is required (0 === lastIndex because of reversed iteration)
        if (groupIndex !== 0) {
          column.hasRightBorder = true;
        }

        column.isSuperHeader = true;

        childColumns.forEach((i, childIndex) => {
          const isFirst = parentIsFirst && childIndex === 0;
          const isLast = parentIsLast && childIndex === childColumns.length - 1;

          // add border if it is last child element (0 === lastIndex)
          i.hasRightBorder = childIndex === childColumns.length - 1 && groupIndex !== 0;

          i.isSubheader = true;
          i.isFirst = isFirst;
          i.isLast = isLast;
        });

        this.configureSecondLevelHeadersHook.forEach(i => i(column, childColumns, childColumns));
        return childColumns.slice().reverse();
      },

      getHeader (index) {
        return this.$refs[`header-${index}`] || null;
      },

      /** RENDERING **/

      /**
       * set table body css parameter
       * @param variableName {String} - name of parameter
       * @param variableValue {String} - value of parameter
       */
      setCssProp (variableName, variableValue) {
        this.$el.querySelector('.m-table__body').style.setProperty(variableName, variableValue);
      },

      renderHeaders (h) {
        const headerRows = this.headersPrepared;

        const getHeaderNodePrams = (column) => {
          const propValues = {
            ...this.columnsMap[column.itemKey],
            ...column,
            ...this.headerProps,
            fontSize: this.fontSize,
            tableParams: this.tableParams,
            allSelected: this.isAllSelected,
            isFirst: this.firstColumnKey === column.itemKey,
            isLast: this.lastColumnKey === column.itemKey,
            disableSortingPredicate: this.disableSortingPredicate,
            lazyResize: column.lazyResize || this.columnLazyResize
          };

          return {
            props: propValues,
            on: {
              ...column.onHeader,
              ...this.headerEventsListeners
            }
          };
        };

        return headerRows.map((row, rowIndex) => {
          const rowColumns = row.map((cell, index) => {
            this.beforeHeaderRendered(cell);
            const itemKey = cell.itemKey || `empty-${index}`;

            const headerComponent = cell.headerComponent;

            const params = getHeaderNodePrams(cell);
            params.props.headerRowIndex = rowIndex;

            const renderedHeader = h(MTableHeader, {
              ...params,
              ref: `header-${itemKey}`,
              key: `${itemKey}-${index}`
            }, [h(headerComponent, params)]);

            this.afterHeaderRendered(renderedHeader);
            return renderedHeader;
          });

          return h(MTableHeaderRow, {slot: 'table-header'}, rowColumns);
        });
      },

      getCellParams (column, columns, row, rowIndex, isLastRow = false, isNested = false) {
        const columnKeys = columns.map(c => c.itemKey);

        return {
          props: {
            ...column,
            columnKeys,
            tableParams: this.tableParams,
            tableRow: row,
            index: rowIndex,
            selectedIds: this.selectedIds,
            selectedIdsSet: this.selectedIdsSet,
            openedRowsKeys: this.openedRowsKeys,
            openedNestedRows: this.openedNestedRows,
            isFirst: this.firstColumnKey === column.itemKey,
            isLast: this.lastColumnKey === column.itemKey,
            withSummaryRow: this.withSummaryRow,
            isAllSelected: this.isAllSelected,
            isAllNestedOpened: this.isAllNestedOpened,
            isNested,
            isLastRow
          },
          on: {
            ...column.on,
            ...this.cellEventsListeners
          }
        };
      },

      renderRow (h, row, rowIndex, columns, tableData, isNestedTable = false, cellKeyPrefix = 'cell', rowKeyPrefix = 'row', tableRowComponent = MTableRow, extraRowProps = {}) {
        const rowUniqueIndex = row && row[this.uniqueKeyAttr] ? row[this.uniqueKeyAttr] : rowIndex;

        const renderedCells = columns.map((column, index) => {

          if (!isNestedTable) {
            // used in MTableStickyMixin
            this.beforeCellRendered(column, row, rowIndex, index, tableData, columns);
          }

          const itemKey = column.itemKey;
          const cellComponent = column.cellComponent;

          const params = this.getCellParams(column, columns, row, rowIndex,
                                            rowIndex + 1 === tableData.length, isNestedTable);

          return h(MTableCellContainer, {
            ...params,
            key: `${cellKeyPrefix}-${index}-${itemKey}`
          }, [h(cellComponent, params)]);
        });

        return h(
          tableRowComponent,
          {
            key: `${rowKeyPrefix}-${rowUniqueIndex}`,
            index: rowIndex,
            slot: 'table-content',
            props: {
              tableRow: row,
              index: rowIndex,
              colorFunction: this.colorFunction,
              enabledKey: this.enabledKey,
              uniqueKeyAttr: this.uniqueKeyAttr,
              openedRowsKeys: this.openedRowsKeys,
              openedNestedRows: this.openedNestedRows,
              withSummaryRow: this.withSummaryRow,
              ...extraRowProps
            }
          },
          renderedCells
        );
      },

      renderRows (h, tableData, columns, isNestedTable = false, cellKeyPrefix = 'cell', rowKeyPrefix = 'row', tableRowComponent = MTableRow, extraRowProps = {}) {
        return tableData.map((row, rowIndex) => {
          const renderedRow = this.renderRow(
            h, row, rowIndex, columns, tableData, isNestedTable,
            cellKeyPrefix, rowKeyPrefix, tableRowComponent, extraRowProps
          );

          return this.processAfterRowRendered([renderedRow], h, row, rowIndex, isNestedTable);
        }).flat();
      },

      // internal function SHOULD NOT BE called from mixins
      _renderRows (h, columns) {
        const processedData = this.data;

        const rows = this.renderRows(h, processedData, columns, false);

        // render row that adds padding after header before all rows
        const paddingRow = h(
          MTableRow,
          {
            key: 'row-padding',
            slot: 'table-content',
            props: {
              index: -1,
              tableRow: {},
              colorFunction: this.colorFunction,
              enabledKey: this.enabledKey,
              uniqueKeyAttr: this.uniqueKeyAttr,
              openedRowsKeys: this.openedRowsKeys
            }
          },
          columns.map((column, index) => {
            const itemKey = column.itemKey;
            return h(MTableCellSpacer, {
              ...this.getCellParams(column, columns, {}, -1),
              key: `cell-spacer-${index}-${itemKey}`
            });
          })
        );

        return [paddingRow, ...rows];
      },

      /**
       * Returns headers and rows list
       * There are multiple stages in table render:
       *    1. Render of headers
       *    2. Render of data cells
       * Every data row (and headers row) is rendered in row container
       * Than this row are rendered in table body
       * @param h - renderFunction
       * @returns {VNode[]} of table body
       */
      getTableContents (h) {

        // get columns and filter text components (newline symbols, spaces and other)
        const columns = this.columnsFlat;
        this.orderedIndexes = columns.map(i => i.itemKey);

        this.callColumnProcessors(columns, h);

        const headerRow = this.renderHeaders(h);

        const rows = this._renderRows(h, columns);

        // render table body with headers and rows
        return [...headerRow, ...rows];
      }
    },

    render (h) {
      this.beforeRender(h);

      const renderedTable = h(MTableBody, {
        class: this.class,
        props: {
          columnsFlat: this.columnsFlat,
          superHeaderNodeKeys: this.superHeaderNodeKeys,
          columnParams: {...this.superHeaderParams, ...this.tableParams},
          withSummaryRow: this.withSummaryRow,
          allowHeaderWordWrap: this.allowHeaderWordWrap
        }
      }, this.getTableContents(h));

      this.afterRender(renderedTable, h);

      return renderedTable;
    }
  };
</script>
